/**
 * AEB (San Polo, Brescia) — the FERMOALE / FERMOLAGER / FERMO Brew range of
 * active dry brewing yeast, sold worldwide and through AEB's own US arm in
 * Lodi, California.
 *
 * Figures come from AEB's own product pages on `aeb-group.com` (the `/en`
 * storefront, or `/us` for the three products only that market carries), each
 * of which prints a "Brewing properties" block: strain, origin, apparent
 * attenuation, fermentation temperature in °C and °F, flocculation, alcohol
 * tolerance and a STA-1 result. Temperatures here are the published °C values,
 * unconverted. Where a page prints a single attenuation figure — AY4 at 84 %,
 * New-E at 78 %, FERMOLAGER W at 82 % — it is stored as a ±2 band, because the
 * headline number assumes AEB's pitch rate and wort. Where alcohol tolerance is
 * printed as a span (9–10 %, 10–11 %) the mid-point is stored.
 *
 * What AEB does not publish, and how it is marked:
 *
 * - **No dosage anywhere.** Neither the product pages, the 2025 beer brochure
 *   nor the per-product compliance data sheets (the only PDF AEB hosts per
 *   product, and a purity/allergen document rather than a technical one) give a
 *   g/hL pitching rate, so `dosageGramsPerHl` is absent on every entry here.
 *   The `100 g/hL` that appears in the compliance sheets is the inoculation
 *   used for AEB's own bacteria counts, not a recommendation.
 * - **FERMOALE Bel-Abbey** has no properties block on its product page; its
 *   numbers are AEB's own 2025 beer brochure
 *   (`/media/aeb-brewing/landing/brochure/2025/Brochure-Beer-2025-v.3-digital-EN.pdf`),
 *   which prints the same block per product. It is `spec-sheet` on that basis,
 *   with `sta1` left `null` because neither document states a result.
 * - **FERMO Brew Citrus and Fruity** are covered in that brochure only by a
 *   shared "Citrus, Fruity & Nectar" panel, so their numbers are the family's
 *   rather than the strain's: `representative`.
 * - **FERMO Brew Berry, FERMO Brew Fragrance, FERMO R03 and WEISS Arome+** are
 *   described in prose alone. They carry the aromatic-family band (Berry,
 *   Fragrance) or class-typical values for a refermentation and a weizen strain
 *   (R03, Arome+), all `representative`, and `alcoholTolerancePercent` is
 *   `null` on the two with nothing published.
 *
 * AEB sells no cider yeast into its beer range; the cider and wine strains sit
 * in the oenology catalogue and are not marketed to brewers, so none are here.
 */

import type { Yeast } from '../../schema/yeast';

/** AEB brewing yeast. @see Yeast */
export const aebYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // FERMOLAGER — bottom fermenting
  // -------------------------------------------------------------------------
  {
    id: 'aeb-fermolager-w',
    lab: 'AEB',
    productCode: 'W',
    name: 'FERMOLAGER W',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 9.5,
    sta1: false,
    description:
      "AEB's workhorse lager strain and the one their own substitution guide lines up against W-34/70: a German bottom-fermenting yeast that finishes clean, drops hard and suits everything from Pilsner and Helles through Märzen and Bock. Esters and phenols are both minimal, so the malt bill carries the beer.",
    tags: ['lager', 'pilsner', 'helles', 'marzen', 'bock', 'clean', 'workhorse'],
    url: 'https://www.aeb-group.com/en/fermolager-w-5206',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermolager-berlin',
    lab: 'AEB',
    productCode: 'Berlin',
    name: 'FERMOLAGER Berlin',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A north-German lager strain with more ester lift than AEB’s other bottom fermenters, which is what makes it their pick for strong lagers: the fruit balances the alcohol in a Bock or Doppelbock instead of leaving it bare. Equally at home in Pils, Helles and dark lagers.',
    tags: ['lager', 'pils', 'schwarzbier', 'bock', 'doppelbock', 'estery'],
    url: 'https://www.aeb-group.com/en/fermolager-berlin-5208',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermolager-crisp',
    lab: 'AEB',
    productCode: 'Crisp',
    name: 'FERMOLAGER Crisp',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Mexico City strain built for adjunct and rice lagers: it attenuates hard at the cold end of the lager band and leaves almost nothing behind, so the finish is dry and the aftertaste short. The obvious choice for a dry-hopped lager or an India pale lager where hops need clear air.',
    tags: ['lager', 'mexican-lager', 'adjunct', 'dry-finish', 'india-pale-lager', 'clean'],
    url: 'https://www.aeb-group.com/en/fermolager-crisp-15194',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermolager-munich-heritage',
    lab: 'AEB',
    productCode: 'Munich Heritage',
    name: 'FERMOLAGER Munich Heritage',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 78, max: 80 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9.5,
    sta1: false,
    description:
      'A Munich house strain that keeps diacetyl, sulfur and higher alcohols all low, leaving a smooth malty frame for noble hops to sit on. AEB pitch it as the everyday lager yeast for a German or central-European range — Helles and Pilsner through Märzen, Dunkles and Bock.',
    tags: ['lager', 'helles', 'marzen', 'dunkel', 'bock', 'noble-hops', 'malty'],
    url: 'https://www.aeb-group.com/en/fermolager-munich-heritage-22564',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // FERMOALE — top fermenting
  // -------------------------------------------------------------------------
  {
    id: 'aeb-fermoale',
    lab: 'AEB',
    productCode: 'FERMOALE',
    name: 'FERMOALE',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The plain-named British strain of the range, aimed at bitters, milds, brown ales, porters and wee heavy. Malt-driven with apple and orange fruit and enough diacetyl for a light toffee note. It flocculates poorly for an English yeast, so give it time or finings before packaging.',
    tags: ['english-ale', 'bitter', 'mild', 'porter', 'wee-heavy', 'malty', 'toffee'],
    url: 'https://www.aeb-group.com/en/fermoale-5205',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-ay3',
    lab: 'AEB',
    productCode: 'AY3',
    name: 'FERMOALE AY3',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'AEB’s most versatile ale yeast and their answer to the classic London dry ale strain: biscuit and soft caramel on the malt side with just enough citrus to keep it lively, and hard flocculation that leaves the beer bright. Carries English, American and Belgian-leaning ales alike.',
    tags: ['english-ale', 'pale-ale', 'brown-ale', 'porter', 'biscuit', 'caramel', 'clearing'],
    url: 'https://www.aeb-group.com/en/fermoale-ay3-5207',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-ay4',
    lab: 'AEB',
    productCode: 'AY4',
    name: 'FERMOALE AY4',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 82, max: 86 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10.5,
    sta1: false,
    description:
      'The Californian strain in dry form — AEB trace it to Chico and line it up against US-05 and BRY-97. Attenuates to 84 %, throws mild esters and a clean citrus finish, then drops firm. The default when an American pale ale or IPA should taste only of malt and hops.',
    tags: ['american-ale', 'chico', 'ipa', 'pale-ale', 'neutral', 'clean', 'workhorse'],
    url: 'https://www.aeb-group.com/en/fermoale-ay4-5210',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-new-e',
    lab: 'AEB',
    productCode: 'New-E',
    name: 'FERMOALE New-E',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10.5,
    sta1: false,
    description:
      'The East Coast strain of the range, built for hazy and juicy IPA: it lifts hop-derived mango, passionfruit and citrus, stops short of dry and stays in suspension, which is where the soft, full mouthfeel comes from. Also AEB’s recommendation for pastry stouts.',
    tags: ['hazy', 'neipa', 'ipa', 'tropical', 'full-body', 'pastry-stout', 'biotransformation'],
    url: 'https://www.aeb-group.com/en/fermoale-new-e-14722',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-german-k',
    lab: 'AEB',
    productCode: 'German K',
    name: 'FERMOALE German K',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A Cologne Kölsch strain that ferments cool and finishes dry and clean, with only a whisper of pear, apple and citrus and next to no diacetyl. Made for Kölsch and Alt, but its cool floor around 13 °C also makes it a workable warm-fermented pseudo-lager yeast.',
    tags: ['kolsch', 'altbier', 'hybrid', 'clean', 'crisp', 'pseudo-lager'],
    url: 'https://www.aeb-group.com/en/fermoale-german-k-15497',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-weiss',
    lab: 'AEB',
    productCode: 'Weiss',
    name: 'FERMOALE WEISS',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9.5,
    sta1: false,
    description:
      'A Bavarian wheat strain out of Freising, POF-positive and balanced between banana ester and clove-pepper phenol. AEB’s own steer: ferment warm, underpitch and aerate less for more banana, do the opposite for more spice. Suits everything from Kristallweizen to Weizenbock.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'weizenbock', 'phenolic', 'estery'],
    url: 'https://www.aeb-group.com/en/fermoale-weiss-22552',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermoale-bel-abbey',
    lab: 'AEB',
    productCode: 'Bel-Abbey',
    name: 'FERMOALE Bel-Abbey',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 16, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'AEB’s Belgian abbey strain, which they trace to Rochefort: figs, raisins and dates over a spicy phenolic base, clean rather than rustic, and tolerant enough of gravity for tripel and quadrupel. Numbers here are from AEB’s beer brochure, since the product page prints prose only.',
    tags: ['belgian', 'abbey', 'dubbel', 'tripel', 'quadrupel', 'phenolic', 'dried-fruit'],
    url: 'https://www.aeb-group.com/en/fermoale-bel-abbey-6880',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // FERMO — kveik, souring and refermentation
  // -------------------------------------------------------------------------
  {
    id: 'aeb-fermo-kveik-sv',
    lab: 'AEB',
    productCode: 'Kveik SV',
    name: 'FERMO Kveik SV',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 34, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'The Voss kveik in dry form, published for a fermentation band of 34–40 °C — hot enough that a summer brewday needs no cooling at all, and quick enough to be done in two or three days. Soft orange peel and citrus, high alcohol tolerance, and a firm drop when it finishes.',
    tags: ['kveik', 'voss', 'farmhouse', 'fast', 'citrus', 'orange', 'high-gravity'],
    url: 'https://www.aeb-group.com/en/fermo-kveik-sv-15308',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermo-brew-acid',
    lab: 'AEB',
    productCode: 'Brew Acid',
    name: 'FERMO Brew Acid',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 19, max: 25 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'A Lachancea thermotolerans out of the Dijon vine and wine institute: it makes lactic acid and alcohol in the same vessel, so a Gose, Berliner Weisse or fruited sour needs neither a kettle-souring step nor a bacterial pitch. Acidity is rounded rather than sharp, and pitch rate sets how far it goes.',
    tags: ['sour', 'lactic', 'wild', 'non-saccharomyces', 'gose', 'berliner-weisse', 'fruited'],
    url: 'https://www.aeb-group.com/en/fermo-brew-acid-6438',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermo-r03',
    lab: 'AEB',
    productCode: 'R03',
    name: 'FERMO R03',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A conditioning strain, not a primary one: it eats simple sugars fast but cannot touch maltotriose, so the priming dose is the whole of what ferments and the package carbonates predictably. Sediments compactly enough to pour past. AEB publish no attenuation or temperature figures, so those are class-typical.',
    tags: ['bottle-conditioning', 'refermentation', 'cask', 'keg', 'neutral', 'clearing'],
    url: 'https://www.aeb-group.com/en/fermo-r03-5204',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // FERMO Brew — aromatic strains for specialty and low-alcohol beer
  // -------------------------------------------------------------------------
  {
    id: 'aeb-fermo-brew-nectar',
    lab: 'AEB',
    productCode: 'Brew Nectar',
    name: 'FERMO Brew Nectar',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 69 },
    temperatureC: { min: 11, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'An AEB hybrid that releases thiols early and phenols late, giving peach, nectarine and a candy note. It attenuates deliberately little and makes very little alcohol for the sugar it takes, which is why AEB point it at low- and no-alcohol beer; correct the pH if you stop it short.',
    tags: ['aromatic', 'low-alcohol', 'non-alcoholic', 'thiols', 'peach', 'nectarine', 'phenolic'],
    url: 'https://www.aeb-group.com/en/fermo-brew-nectar-6439',
    provenance: 'spec-sheet',
  },
  {
    id: 'aeb-fermo-brew-citrus',
    lab: 'AEB',
    productCode: 'Brew Citrus',
    name: 'FERMO Brew Citrus',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 69 },
    temperatureC: { min: 11, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A hybridised aromatic strain that throws citrus and herbal notes early in fermentation, so a beer stopped below 0.5 % ABV still smells of something. Nutrition matters: AEB pair it with their own FERMOPLUS range to push the aroma. Figures are the ones AEB publish for the Citrus/Fruity/Nectar group.',
    tags: ['aromatic', 'citrus', 'herbal', 'low-alcohol', 'non-alcoholic', 'specialty'],
    url: 'https://www.aeb-group.com/en/fermo-brew-citrus-6442',
    provenance: 'representative',
  },
  {
    id: 'aeb-fermo-brew-fruity',
    lab: 'AEB',
    productCode: 'Brew Fruity',
    name: 'FERMO Brew Fruity',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 69 },
    temperatureC: { min: 11, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The tropical member of the aromatic hybrids: summer and tropical fruit character developed early enough to survive an interrupted fermentation, which is how AEB use it for low- and no-alcohol beer. Figures are the ones AEB publish for the Citrus/Fruity/Nectar group rather than for this strain alone.',
    tags: ['aromatic', 'tropical', 'low-alcohol', 'non-alcoholic', 'specialty', 'fruity'],
    url: 'https://www.aeb-group.com/en/fermo-brew-fruity-6444',
    provenance: 'representative',
  },
  {
    id: 'aeb-fermo-brew-berry',
    lab: 'AEB',
    productCode: 'Brew Berry',
    name: 'FERMO Brew Berry',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 69 },
    temperatureC: { min: 11, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A hybridised aromatic strain aimed at blueberry, blackcurrant and raspberry character, with a low nutritional demand that gets the aroma out early. Sold through AEB USA. Nothing but prose is published for it, so the numbers here are the aromatic family’s rather than this strain’s.',
    tags: ['aromatic', 'berry', 'blackcurrant', 'raspberry', 'low-alcohol', 'specialty'],
    url: 'https://www.aeb-group.com/us/fermo-brew-berry-6586',
    provenance: 'representative',
  },
  {
    id: 'aeb-fermo-brew-fragrance',
    lab: 'AEB',
    productCode: 'Brew Fragrance',
    name: 'FERMO Brew Fragrance',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 65, max: 69 },
    temperatureC: { min: 11, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The floral one: white blossom and a minty lift, developed early in fermentation so it survives being stopped short for a low- or no-alcohol beer. Sold through AEB USA. No properties block is published, so the numbers here are the aromatic family’s rather than this strain’s.',
    tags: ['aromatic', 'floral', 'mint', 'low-alcohol', 'non-alcoholic', 'specialty'],
    url: 'https://www.aeb-group.com/us/fermo-brew-fragrance-6441',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Outside the FERMO naming
  // -------------------------------------------------------------------------
  {
    id: 'aeb-weiss-arome-plus',
    lab: 'AEB',
    productCode: 'Weiss Arome+',
    name: 'WEISS Arome+',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A wheat-beer strain AEB sell alongside the FERMO range, making clove phenol and banana ester across hefeweizen, American wheat and Belgian witbier. Sold through AEB USA. AEB publish no properties block for it at all, so every number here is class-typical for a weizen strain rather than measured.',
    tags: ['wheat', 'hefeweizen', 'witbier', 'banana', 'clove', 'phenolic', 'estery'],
    url: 'https://www.aeb-group.com/us/weiss-arome-15199',
    provenance: 'representative',
  },
];
